// A match board's rules, shared by the web and native views: tap one side,
// then the other; a pair lands when both name the same key, a wrong pair
// shows as a miss until the view clears it.

export type MatchSide = "L" | "R";

export interface MatchBoard {
	/** The picked left and right ids, if any. */
	a: string | null;
	b: string | null;
	matched: string[];
	/** The wrong pair on show, [left, right]; taps wait until it clears. */
	miss: [string, string] | null;
}

export const MATCH_START: MatchBoard = { a: null, b: null, matched: [], miss: null };

/** How long a wrong pair shows before the board clears it. */
export const MISS_MS = 550;

export interface MatchTap {
	board: MatchBoard;
	/** A pair was tried: its left key, scored, and on a miss the right-hand tile picked. */
	score: { key: string; ok: boolean; picked?: string } | null;
	/** The last pair just landed. */
	complete: boolean;
}

/** null when the tap does nothing: a miss is on show, or the tile is already matched. */
export function tapMatch(
	board: MatchBoard,
	side: MatchSide,
	id: string,
	pairs: number,
): MatchTap | null {
	if (board.miss || board.matched.includes(id)) return null;
	const a = side === "L" ? id : board.a;
	const b = side === "R" ? id : board.b;
	if (a === null || b === null) return { board: { ...board, a, b }, score: null, complete: false };
	if (a === b) {
		const matched = [...board.matched, a];
		return {
			board: { a: null, b: null, matched, miss: null },
			score: { key: a, ok: true },
			complete: matched.length === pairs,
		};
	}
	// A miss scores against the left key, the one the learner was asked about.
	return {
		board: { ...board, a, b, miss: [a, b] },
		score: { key: a, ok: false, picked: b },
		complete: false,
	};
}

export const clearMiss = (board: MatchBoard): MatchBoard => ({
	...board,
	a: null,
	b: null,
	miss: null,
});

export function matchTileState(
	board: MatchBoard,
	side: MatchSide,
	id: string,
): "idle" | "selected" | "wrong" | "done" {
	if (board.matched.includes(id)) return "done";
	if (board.miss && board.miss[side === "L" ? 0 : 1] === id) return "wrong";
	if ((side === "L" ? board.a : board.b) === id) return "selected";
	return "idle";
}
